import { useState, useRef, useEffect, useCallback } from 'react';
import { Volume2, VolumeX } from 'lucide-react';
import { motion } from 'framer-motion';
import { TEST_IDS } from '@/constants/testIds';

const AUDIO_URL = 'https://customer-assets.emergentagent.com/job_f54f3a20-4df8-4151-89bb-3d96c747d48f/artifacts/45eeaw0y_Website%20Jingle.mp3';
const FADE_MS = 1800;
const STEPS = 40;
const MAX_VOL = 0.55; // 55% — comfortable default, user can raise device volume

// ── Singleton: one audio element no matter how many components mount ──
let _audio = null;
let _fade = null;
function getAudio() {
  if (!_audio) { _audio = new Audio(AUDIO_URL); _audio.loop = true; _audio.preload = 'auto'; _audio.volume = 0; }
  return _audio;
}
function stopFade() { if (_fade) { clearInterval(_fade); _fade = null; } }

export default function AudioPlayer() {
  const [playing, setPlaying] = useState(false);
  const wasOnRef = useRef(false);     // was audio on before leaving?
  const gesturedRef = useRef(false);  // has user ever started audio?

  // ── Fade in: 0 → MAX_VOL over FADE_MS ──
  const fadeIn = useCallback(() => {
    const a = getAudio();
    stopFade();
    a.volume = 0;
    const p = a.play();
    if (!p) return;
    p.then(() => {
      let v = 0;
      _fade = setInterval(() => {
        v = Math.min(MAX_VOL, v + MAX_VOL / STEPS);
        a.volume = v;
        if (v >= MAX_VOL) stopFade();
      }, FADE_MS / STEPS);
      setPlaying(true);
      gesturedRef.current = true;
    }).catch(() => {});
  }, []);

  // ── Fade out: current → 0, then pause ──
  const fadeOut = useCallback(() => {
    const a = getAudio();
    stopFade();
    let v = a.volume;
    if (v <= 0 || a.paused) { a.pause(); a.volume = 0; setPlaying(false); return; }
    _fade = setInterval(() => {
      v = Math.max(0, v - MAX_VOL / STEPS);
      a.volume = v;
      if (v <= 0.01) { stopFade(); a.volume = 0; a.pause(); setPlaying(false); }
    }, FADE_MS / STEPS);
    setPlaying(false);
  }, []);

  // ── 1) First-time auto-play on any interaction ──
  useEffect(() => {
    const trigger = () => {
      if (gesturedRef.current) return;
      gesturedRef.current = true; // sync guard — prevents re-entry
      // Remove listeners immediately
      ['click','touchstart','touchend','keydown','wheel'].forEach(e => document.removeEventListener(e, trigger));
      const a = getAudio();
      a.volume = 0;
      const p = a.play();
      if (p) {
        p.then(() => {
          let v = 0;
          stopFade();
          _fade = setInterval(() => {
            v = Math.min(MAX_VOL, v + MAX_VOL / STEPS);
            a.volume = v;
            if (v >= MAX_VOL) stopFade();
          }, FADE_MS / STEPS);
          setPlaying(true);
        }).catch(() => {
          // blocked — re-add listeners for retry
          gesturedRef.current = false;
          ['click','touchstart','touchend','keydown','wheel'].forEach(e =>
            document.addEventListener(e, trigger, { passive: true })
          );
        });
      }
    };
    ['click','touchstart','touchend','keydown','wheel'].forEach(e =>
      document.addEventListener(e, trigger, { passive: true })
    );
    return () => {
      ['click','touchstart','touchend','keydown','wheel'].forEach(e => document.removeEventListener(e, trigger));
    };
  }, []);

  // ── 2) Auto-pause on leave, auto-resume on return ──
  useEffect(() => {
    let resumeListener = null;

    const tryResume = () => {
      const a = getAudio();
      a.volume = 0;
      const p = a.play();
      if (!p) return;
      p.then(() => {
        let v = 0;
        stopFade();
        _fade = setInterval(() => {
          v = Math.min(MAX_VOL, v + MAX_VOL / STEPS);
          a.volume = v;
          if (v >= MAX_VOL) stopFade();
        }, FADE_MS / STEPS);
        setPlaying(true);
        wasOnRef.current = false;
        // clean up resume listener if any
        if (resumeListener) {
          ['click','touchstart','touchend'].forEach(e => document.removeEventListener(e, resumeListener));
          resumeListener = null;
        }
      }).catch(() => {
        // Browser blocked auto-resume — wait for next touch to resume
        if (!resumeListener) {
          resumeListener = () => {
            if (wasOnRef.current) tryResume();
          };
          ['click','touchstart','touchend'].forEach(e =>
            document.addEventListener(e, resumeListener, { once: true, passive: true })
          );
        }
      });
    };

    const onVisChange = () => {
      const a = getAudio();
      if (document.hidden) {
        // ── LEAVING: fade out fast + pause ──
        wasOnRef.current = !a.paused;
        stopFade();
        a.volume = 0;
        a.pause();
        setPlaying(false);
      } else {
        // ── RETURNING: auto-resume if was playing ──
        if (wasOnRef.current) tryResume();
      }
    };

    document.addEventListener('visibilitychange', onVisChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisChange);
      if (resumeListener) {
        ['click','touchstart','touchend'].forEach(e => document.removeEventListener(e, resumeListener));
      }
    };
  }, []);

  // ── Toggle button — INSTANT response, no fade delay ──
  const toggle = useCallback((e) => {
    e.stopPropagation();
    const a = getAudio();
    if (!a.paused) {
      // MUTE — instant
      stopFade();
      a.pause();
      a.volume = 0;
      setPlaying(false);
      wasOnRef.current = false;
    } else {
      // UNMUTE — instant
      a.volume = MAX_VOL;
      const p = a.play();
      if (p) {
        p.then(() => { setPlaying(true); gesturedRef.current = true; })
         .catch(() => {});
      }
    }
  }, []);

  return (
    <motion.button
      onClick={toggle}
      data-testid={TEST_IDS.audioToggleBtn}
      className="flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-[0.15em] font-sans text-[#1B2A4A] hover:text-[#C9A96E] transition-colors duration-300"
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      aria-label={playing ? 'Mute audio' : 'Play audio'}
    >
      {playing ? (
        <>
          <Volume2 className="w-4 h-4" />
          <span className="flex items-end gap-[2px] h-3">
            {[1, 2, 3].map(i => (
              <motion.span key={i} className="w-[2px] bg-[#C9A96E] rounded-full"
                animate={{ height: ['4px', '12px', '6px', '10px', '4px'] }}
                transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.15, ease: 'easeInOut' }}
              />
            ))}
          </span>
        </>
      ) : (
        <VolumeX className="w-4 h-4" />
      )}
      <span className="hidden sm:inline">{playing ? 'On' : 'Off'}</span>
    </motion.button>
  );
}
